"use client";

import { memo, useMemo, useEffect, useState } from "react";
import { useRouter } from "next/navigation";
import useSWR from "swr";
import { format } from "date-fns";
import { useForm } from "react-hook-form";
import { format as jFormat } from "date-fns-jalali";
import { flightSchema } from "@/shared/schemas/flight/flightTab.Schema";
import type { FlightSchemaType } from "@/shared/schemas/flight/flightTab.Schema";
import { yupResolver } from "@hookform/resolvers/yup";
import apis from "@/configs/apis";
import TabContent from "../TabContent";
import useProgress from "@/shared/hooks/useProgress";
import { jalaliToGregorian } from "@/shared/libs/date";
import { flightTabIcons } from "@/shared/constants/icons";
import FlightType from "./components/FlightType";
import { DrawerClose } from "@/shared/components/common/drawer/drawer";
import { PopoverClose } from "@/shared/components/common/popover";
import fetcher, { type ExtendedResponse } from "@/shared/libs/fetcher";
import { useDeviceType } from "@/shared/context/DeviceContext";
import useDebounce from "@/shared/hooks/useDebounce";
type TFormField = "origin" | "destination" | "date" | "passengers";
interface FlightTabProps {
  page?: "landing" | "result" | "passengers" | "confirm";
  searchParams?: SearchParams;
}
import Datepicker from "@/shared/components/common/datepicker/Datepicker";
import PassengerPicker from "../../passengerpicker/PassengerPicker";
import LocationPicker from "../../locationPicker";
export default memo(function FlightTab({
  page = "result",
  searchParams
}: FlightTabProps) {
  const userDeviceType = useDeviceType();
  const router = useRouter();
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebounce(search, 500);
  const {
    data: airportsResponse,
    mutate,
    isLoading: airportsLoading
  } = useSWR<ExtendedResponse<AirportRes>>(`${apis.flight.airports.client}?q=${debouncedSearch}`, fetcher, {
    revalidateOnMount: true,
    revalidateIfStale: false,
    revalidateOnFocus: true,
    revalidateOnReconnect: false
  });
  const {
    data: airports
  } = airportsResponse ?? {};
  const {
    adult,
    child,
    infant,
    depart
  } = searchParams ?? {};
  const originType = searchParams?.origin ? "city" : "airport";
  const destinationType = searchParams?.destination ? "city" : "airport";
  const form = useForm({
    resolver: yupResolver(flightSchema),
    defaultValues: {
      passengers: {
        adult: adult ? +adult : 1,
        child: child ? +child : 0,
        infant: infant ? +infant : 0
      },
      date: depart ? {
        depart: depart ? jFormat(depart, "yyyy/M/d") : "",
        return: searchParams?.return ? jFormat(searchParams?.return, "yyyy/M/d") : ""
      } : undefined,
      flightType: searchParams?.flightType ?? "one-way",
      origin: undefined,
      destination: undefined
    }
  });
  const {
    setValue,
    getValues,
    watch,
    control
  } = form;
  const {
    passengers
  } = getValues();
  const origin = watch("origin");
  const destination = watch("destination");
  const date = watch("date");
  const calendarUrl = useMemo(() => {
    if (origin && destination) {
      const originParam = origin?.type === "city" ? "origin_city_id=" : "origin_id=";
      const destinationParam = destination?.type === "city" ? "destination_city_id=" : "destination_id=";
      const date = format(new Date(), "yyyy-MM-dd");
      return `${apis.flight.calendar.client}?${originParam}${origin?.id}&${destinationParam}${destination?.id}&date=${date}`;
    }
    return null;
  }, [origin, destination]);
  const {
    data: calendarResponse,
    isLoading,
    mutate: updatePriceCalendar
  } = useSWR<ExtendedResponse<CalendarRes>>(calendarUrl, fetcher, {
    revalidateOnMount: false,
    revalidateIfStale: false,
    revalidateOnFocus: false,
    revalidateOnReconnect: false
  });
  const {
    data: calendar
  } = calendarResponse ?? {};
  const flightType = watch("flightType");
  const progressInputs = [{
    id: 0,
    name: "origin",
    value: watch("origin.id")
  }, {
    id: 1,
    name: "destination",
    value: watch("destination.id")
  }, {
    id: 2,
    name: "date",
    value: watch("date.depart")
  }, {
    id: 3,
    name: "passengers",
    value: Object.values(watch("passengers")).find(item => !!item)
  }];
  const {
    handleProgress,
    openStatus
  } = useProgress(progressInputs);
  const calendarOpenStatus = openStatus[2];
  const handleSetValues = ({
    paramName,
    id,
    func
  }: {
    paramName: TFormField;
    id: number;
    func?: (value: any) => any;
  }) => (value: any) => {
    if (func) {
      setValue(paramName, func(value));
    } else {
      setValue(paramName, value);
    }
    id !== 3 && handleProgress(id, true);
    setSearch("");
  };
  const inputs = (key: string, paramName: TFormField, id: number, func?: (value: any) => string) => {
    const searchResult = {
      data: airports?.results,
      isLoading: airportsLoading,
      search,
      setSearch: (e: React.ChangeEvent<HTMLInputElement>) => setSearch(e.target.value)
    };
    const inputObj = {
      origin: <LocationPicker onClick={handleSetValues({
        paramName,
        id,
        func
      })} OnCloseComp={userDeviceType === "mobile" ? DrawerClose : PopoverClose} route="origin" searchResult={searchResult} />,
      destination: <LocationPicker onClick={handleSetValues({
        paramName,
        id,
        func
      })} OnCloseComp={userDeviceType === "mobile" ? DrawerClose : PopoverClose} route="destination" searchResult={searchResult} />,
      date: <Datepicker onPickDate={handleSetValues({
        paramName,
        id,
        func
      })} OnCloseComp={userDeviceType === "mobile" ? DrawerClose : PopoverClose} dayData={calendar?.results ?? []} loading={isLoading} mode={flightType === "one-way" ? "single" : "range"} selectedDate={{
        from: date?.depart ? new Date(jalaliToGregorian(date.depart)) : undefined,
        to: date?.return ? new Date(jalaliToGregorian(date.return)) : undefined
      }} />,
      passengers: <PassengerPicker passengers={passengers} onClick={handleSetValues({
        paramName,
        id,
        func
      })} OnCloseComp={userDeviceType === "mobile" ? DrawerClose : PopoverClose} />
    };
    return inputObj[key as TFormField];
  };
  const onSubmit = (data: FlightSchemaType) => {
    const {
      origin,
      destination,
      date,
      flightType,
      passengers
    } = data;
    const {
      adult,
      child,
      infant
    } = passengers;
    localStorage.setItem("drawerOpen", JSON.stringify(false));
    router.push(`/flights/${origin.name}-${destination.name}/?depart=${jalaliToGregorian(date.depart ?? "")}${flightType === "round-trip" ? `&return=${jalaliToGregorian(data.date?.return ?? "")}` : ""}&adult=${adult}&child=${child}&infant=${infant}${origin.type === "city" ? `&origin=${origin.id}` : `&originAirport=${origin.id}`}${destination.type === "city" ? `&destination=${destination.id}` : `&destinationAirport=${destination.id}`}&flightType=${flightType}`);
  };

  // Search for all cities
  useEffect(() => {
    if (page === "result") {
      mutate();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);
  useEffect(() => {
    if (openStatus[2].open && origin?.id && destination?.id) updatePriceCalendar();
  }, [calendarOpenStatus, destination?.id, openStatus, origin?.id, updatePriceCalendar]);
  useEffect(() => {
    const {
      results
    } = airports ?? {};
    if (searchParams && results) {
      const originData = results.find(({
        id,
        airports
      }) => originType === "city" ? id === +searchParams.origin : airports.find(({
        id
      }) => id === +searchParams.originAirport));
      const destinationData = results.find(({
        id,
        airports
      }) => destinationType === "city" ? id === +searchParams.destination : airports.find(({
        id
      }) => id === +searchParams.destinationAirport));
      const origin = originType === "city" ? originData : originData?.airports.find(({
        id
      }) => id === +searchParams.originAirport);
      const destination = destinationType === "city" ? destinationData : destinationData?.airports.find(({
        id
      }) => id === +searchParams.destinationAirport);
      originData && origin && setValue("origin", {
        name: origin.lang_name,
        code: originData.airports[0].iata,
        id: origin.id,
        type: originType
      });
      destinationData && destination && setValue("destination", {
        name: destinationData?.lang_name,
        code: destinationData.airports[0].iata,
        id: destination.id,
        type: destinationType
      });
    }
  }, [airports, destinationType, originType, searchParams, setValue]);
  return <TabContent<TFormField, FlightSchemaType> page={page} services="flight" className={(id, type) => `${id === 2 ? "col-span-12 sm:col-span-6 lg:col-span-3" : id === 3 ? "col-span-12 sm:col-span-6 lg:col-span-3" : "col-span-12 lg:col-span-2"} ${type !== "origin" && type !== "destination" && "md:px-1"} ${type === "destination" && "md:pl-2"} relative`} hasLocationSwitch headerButton={{
    component: <FlightType control={control} selected={flightType} />,
    selected: flightType
  }} inputConfig={{
    icons: flightTabIcons,
    inputs,
    progressInputs: {
      handleProgress,
      openStatus
    }
  }}
  // @ts-ignore
  form={form}
  // @ts-ignore
  onSubmit={onSubmit} />;
});